<template>
  <v-app>
    <v-main style="display: contents;">
      <div class="sweepstakes-entry">
        <!-- Hero Section with Car Video/Image -->
        <section class="entry-hero">
          <div class="hero-background">
            <div class="hero-media-container">
              <img
                :src="campaign && campaign.hero_image_url ? campaign.hero_image_url : require('@/assets/img/bg-car.jpg')"
                alt="Campaign Hero Image"
                class="hero-image"
                @error="onHeroImageError"
              />
              <div class="hero-overlay"></div>
            </div>

            <v-container class="hero-content" >
              <v-row justify="center" align="center">
                <v-col cols="12" lg="12">
                  <v-row align="center">
                    <!-- Left Side - Video/Image -->
                    <v-col cols="12" md="6" class="text-center">
                      <div class="car-video-container">
                        <div class="video-placeholder" v-if="$vuetify.breakpoint.xs">
                          <img
                            :src="campaign && campaign.prize_image_url ? campaign.prize_image_url : require('@/assets/img/tesla-model-s.png')"
                            alt="Prize Image"
                            class="car-image"
                            @error="onPrizeImageError"
                          />
                          <div class="play-button" @click="playVideo">
                            <v-icon size="60" color="white" class="material-icons-round">play_circle</v-icon>
                          </div>
                          <div class="video-caption">
                            AND YES, YOU'RE LOOKING AT THE ACTUAL CAR
                          </div>
                        </div>

                        <!-- Entries Unlocked Badge -->
                        <div class="entries-badge">
                          <div class="badge-content">
                            <div class="badge-number">{{ totalUnlockedEntries.toLocaleString() }} ENTRIES</div>
                            <div class="badge-label">UNLOCKED</div>
                            <div class="badge-timer">
                              <countdown-timer
                                v-if="campaign && campaign.end_date"
                                :end-time="campaign.end_date"
                                compact
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Right Side - Entry Selection -->
                    <v-col cols="12" md="6">
                      <div class="entry-selection">
                        <div class="step-header mb-4">
                          <h1 class="step-title white--text">
                            ONE MORE STEP...
                            <br>
                            <span class="highlight-text">{{ totalUnlockedEntries.toLocaleString() }} ENTRIES UNLOCKED!</span>
                          </h1>
                          <h2 class="choose-title white--text">CHOOSE A PACK:</h2>
                        </div>

                        <!-- Tier Selection Cards -->
                        <div class="tier-cards">
                          <div
                            v-for="(tier, index) in mockTiers"
                            :key="tier.id"
                            class="tier-card"
                            :class="{
                              'tier-selected': selectedTier && selectedTier.id === tier.id,
                              'tier-popular': tier.popular
                            }"
                            @click="selectTier(tier)"
                          >
                            <div class="tier-header">
                              <div class="tier-icon">
                                <v-icon size="24" color="white">{{ tier.icon }}</v-icon>
                              </div>
                              <div class="tier-info">
                                <div class="tier-entries">{{ tier.entries.toLocaleString() }} Entries</div>
                                <div class="tier-wallpapers">{{ tier.wallpapers }} Wallpapers</div>
                              </div>
                              <div class="tier-price">${{ tier.price }}</div>
                              <div class="tier-action">
                                <v-btn
                                  :color="selectedTier && selectedTier.id === tier.id ? 'success' : 'green'"
                                  class="select-btn"
                                  small
                                  dark
                                  @click.stop="selectTier(tier)"
                                >
                                  {{ selectedTier && selectedTier.id === tier.id ? 'SELECTED' : 'SELECT' }}
                                </v-btn>
                              </div>
                            </div>

                            <!-- Popular Badge -->
                            <div v-if="tier.popular" class="popular-badge">
                              <div class="popular-text">MOST POPULAR</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </section>

        <!-- Bottom CTA Section -->
        <section class="bottom-cta py-16">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="8" class="text-center">
                <div class="cta-content">
                  <h2 class="cta-title mb-4">
                    GET <span class="highlight-text">{{ totalUnlockedEntries.toLocaleString() }} ENTRIES</span> TODAY
                  </h2>
                  <p class="cta-subtitle mb-6">
                    DON'T WORRY, YOUR ENTRIES ARE AUTOMATICALLY TRACKED!
                  </p>

                  <v-btn
                    x-large
                    color="orange"
                    class="cta-button"
                    dark
                    :disabled="!selectedTier"
                    @click="proceedToCheckout"
                  >
                    CONTINUE TO CHECKOUT
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- Loading State -->
        <v-overlay :value="loading">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import CountdownTimer from './CountdownTimer.vue'

export default {
  name: 'SweepstakesEntry',
  components: {
    CountdownTimer
  },
  props: {
    campaignId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      campaign: null,
      tiers: [],
      selectedTier: null,
      bonusEnabled: false,
      referralCode: null, // Store referral code from URL
      mockTiers: [
        {
          id: 1,
          entries: 1750,
          wallpapers: 3,
          price: 15,
          icon: 'mdi-package-variant',
          popular: false
        },
        {
          id: 2,
          entries: 2250,
          wallpapers: 5,
          price: 25,
          icon: 'mdi-package-variant',
          popular: false
        },
        {
          id: 3,
          entries: 2750,
          wallpapers: 7,
          price: 35,
          icon: 'mdi-package-variant',
          popular: true
        },
        {
          id: 4,
          entries: 3500,
          wallpapers: 10,
          price: 50,
          icon: 'mdi-package-variant',
          popular: false
        },
        {
          id: 5,
          entries: 6000,
          wallpapers: 20,
          price: 100,
          icon: 'mdi-package-variant',
          popular: false
        },
        {
          id: 6,
          entries: 8500,
          wallpapers: 30,
          price: 150,
          icon: 'mdi-package-variant',
          popular: false
        }
      ]
    }
  },
  computed: {
    totalUnlockedEntries() {
      return 1000 // Base unlocked entries
    }
  },
  async mounted() {
    // Capture referral code from URL
    this.referralCode = this.$route.query.ref || null
    if (this.referralCode) {
      console.log('Referral code detected:', this.referralCode)
      // Store in localStorage for checkout process
      localStorage.setItem('sweepstakes_referral_code', this.referralCode)
    }

    await this.loadCampaignData()
  },
  methods: {
    async loadCampaignData() {
      try {
        this.loading = true
        
        // Load campaign details
        const campaignResponse = await window.axios.get(`api/sweepstakes/campaign/${this.campaignId}`)
        if (campaignResponse.data.flag === 1) {
          this.campaign = campaignResponse.data.campaign
        }

        // Load tiers
        const tiersResponse = await window.axios.get(`api/sweepstakes/tiers/${this.campaignId}`)
        if (tiersResponse.data.flag === 1) {
          this.tiers = tiersResponse.data.tiers
        }
        
      } catch (error) {
        console.error('Error loading campaign data:', error)
        this.$toast.error('Failed to load sweepstakes information')
        this.$router.push('/sweepstakes')
      } finally {
        this.loading = false
      }
    },
    
    selectTier(tier) {
      this.selectedTier = tier
    },

    playVideo() {
      // Placeholder for video play functionality
      console.log('Playing video...')
    },

    proceedToCheckout() {
      if (this.selectedTier) {
        // Get stored user info
        const userInfo = JSON.parse(localStorage.getItem('sweepstakes_user_info') || '{}')

        this.$router.push({
          name: 'SweepstakesCheckout',
          params: {
            campaignId: this.campaignId,
            tierId: this.selectedTier.id
          },
          query: {
            bonus: this.bonusEnabled ? '1' : '0',
            userInfo: 'stored',
            ref: this.referralCode // Pass referral code to checkout
          }
        })
      }
    },
    
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    
    formatNumber(value) {
      return new Intl.NumberFormat('en-US').format(value)
    },

    onHeroImageError(event) {
      console.warn('Hero image failed to load, using fallback')
      event.target.src = require('@/assets/img/bg-car.jpg')
    },

    onPrizeImageError(event) {
      console.warn('Prize image failed to load, using fallback')
      event.target.src = require('@/assets/img/tesla-model-s.png')
    }
  }
}
</script>

<style scoped>
.sweepstakes-entry {
  min-height: 100vh;
  background: #f8f9fa;
}

/* Hero Section */
.entry-hero {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
}

.hero-background {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.hero-media-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.3;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.8) 0%, rgba(22, 33, 62, 0.7) 50%, rgba(15, 52, 96, 0.8) 100%);
  z-index: 2;
}

.hero-content {
  position: relative;
  z-index: 3;
  min-height: 100vh;
  display: flex;
  align-items: center;
}

/* Car Video Section */
.car-video-container {
  position: relative;
  max-width: 500px;
  margin: 0 auto;
}

.video-placeholder {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.car-image {
  width: 100%;
  height: auto;
  display: block;
}

.play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.play-button:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: translate(-50%, -50%) scale(1.1);
}

.video-caption {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
  text-align: center;
}

/* Entries Badge */
.entries-badge {
  position: absolute;
  top: -20px;
  left: -20px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  border-radius: 15px;
  padding: 16px;
  color: white;
  text-align: center;
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.4);
  min-width: 150px;
}

.badge-number {
  font-size: 1.1rem;
  font-weight: 900;
  line-height: 1;
}

.badge-label {
  font-size: 0.8rem;
  opacity: 0.9;
  margin-bottom: 8px;
}

.badge-timer {
  font-size: 0.9rem;
  font-weight: bold;
}

/* Entry Selection */
.entry-selection {
  padding: 2rem;
}

.step-title {
  font-size: 2.5rem;
  font-weight: 900;
  line-height: 1.1;
  margin-bottom: 1rem;
}

.highlight-text {
  color: #ff6b35;
  background: linear-gradient(45deg, #ff6b35, #f7931e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.choose-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
}

/* Tier Cards */
.tier-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tier-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid transparent;
  position: relative;
  backdrop-filter: blur(10px);
}

.tier-card:hover {
  transform: translateX(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.tier-selected {
  border-color: #4CAF50;
  background: #4CAF50;
  color: white!important;
}

.tier-popular {
  border-color: white;
  background-color: #ff6b35;
  color: white!important;
}

.tier-header {
  display: flex;
  align-items: center;
  gap: 15px;
}

.tier-icon {
  background: #666;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tier-info {
  flex: 1;
}

.tier-entries {
  font-size: 1.1rem;
  font-weight: 900;
  color: #333;
  line-height: 1;
}

.tier-wallpapers {
  font-size: 0.9rem;
  color: #666;
}

.tier-price {
  font-size: 1.5rem;
  font-weight: 900;
  color: #333;
  margin-right: 15px;
}

.select-btn {
  font-size: 0.8rem;
  font-weight: 700;
  padding: 8px 16px !important;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.popular-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: #000;
  color: white;
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Bottom CTA Section */
.bottom-cta {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 900;
  line-height: 1.1;
}

.cta-subtitle {
  font-size: 1rem;
  opacity: 0.8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.cta-button {
  font-size: 1.1rem;
  padding: 18px 40px !important;
  border-radius: 50px;
  text-transform: uppercase;
  font-weight: 900;
  letter-spacing: 1px;
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .step-title {
    font-size: 2rem;
    text-align: center;
  }

  .choose-title {
    font-size: 1.2rem;
    text-align: center;
  }

  .cta-title {
    font-size: 2rem;
  }

  .tier-card {
    margin-bottom: 1rem;
  }

  .tier-header {
    flex-wrap: wrap;
    gap: 10px;
  }

  .tier-price {
    font-size: 1.3rem;
  }

  .car-video-container {
    margin-bottom: 2rem;
  }

  .entries-badge {
    position: relative;
    top: 0;
    left: 0;
    margin: 1rem auto;
    display: inline-block;
  }
}

@media (max-width: 480px) {
  .step-title {
    font-size: 1.5rem;
  }

  .cta-title {
    font-size: 1.5rem;
  }

  .tier-entries {
    font-size: 1rem;
  }
}
</style>
